import { Signal, inject } from '@angular/core';

import {
  QueryClient,
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { catchError, firstValueFrom, of } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/toast.service';

import {
  NomObjectClass,
  NomObjectTypes,
  feedDerivedTimeShown,
} from 'src/app/core/constants';
import { DashboardViewPreset, User } from 'src/app/core/models/core.models';
import {
  FeedSettingsService,
  PropertyViewConfig,
} from 'src/app/core/services/feed-settings.service';
import { HomeSettingsService } from 'src/app/core/services/home-settings.service';
import { UserAccessLevel } from 'src/app/core/services/user.service';
import { WidgetSettingsService } from 'src/app/core/services/widget-settings.service';
import { WorkspaceSettingsService } from 'src/app/core/services/workspace-settings.service';
import {
  AdminViewPreset,
  AdminViewPresetCopy,
  AdminViewPresetUpdate,
  ViewId,
  ViewPreset,
  ViewPresetApiService,
  ViewPresetContext,
  ViewPresetFlags,
} from '../services/view-preset-api.service';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * View presets - the shared tier behind every
 * POST /view_preset/get_by_context_properties read.
 *
 * One endpoint, six contexts (`hv-objects`, `wv-objects`, `pv-widgets`,
 * `pv-feed-results`, `ov-dashboard`, `ov-dashboard-page`) whose `settings` blobs
 * have nothing in common. Hence **one api-service and one save mutation, but a
 * query hook per context**: each blob's post-processing (defaults merge, permission
 * filtering, language fallback) is that context's identity.
 *
 * **The cache is the state.** A toggle patches the cached blob with `setQueryData`
 * and posts the patched value, which is why every query here disables background
 * refetching - a refetch landing on top of a flip would revert it. Nothing rolls
 * back a failed save.
 *
 * **Errors degrade to defaults** rather than surfacing, so `data()` is always
 * eventually defined and no consumer needs a fallback of its own.
 */

/** Scope of a per-user view-options blob (`hv-objects` / `wv-objects`). */
export interface UserViewOptionsScope {
  userId: string;
  /**
   * A key term: the defaults are permission-filtered off `user.feature_components`,
   * which is company-scoped, so this is real identity - and a key change is what
   * makes a live observer refetch.
   */
  companyId: string;
  /**
   * Deliberately **not** a key term: it only feeds the defaults' permission filter,
   * and it is already identified by `userId` + `companyId`.
   */
  user: User;
}

/**
 * `companyId` is passed only for the **user-scoped** blobs, where the same
 * `contextObjectId` (the user) means different things in different companies. An
 * object-scoped context (a dashboard, a feed) is already company-unique, so it omits
 * it - TanStack's key hash drops the undefined entry.
 */
export const viewPresetQueryKey = (
  context: ViewPresetContext,
  companyId?: string,
) =>
  [
    'view_preset',
    context.viewId,
    {
      contextObjectId: context.contextObjectId,
      contextObjectClassId: context.contextObjectClassId,
      companyId,
    },
  ] as const;

const userViewOptionsContext = (
  viewId: ViewId,
  scope: UserViewOptionsScope,
): ViewPresetContext => ({
  viewId,
  contextObjectId: scope.userId,
  contextObjectClassId: NomObjectClass.USER,
});

/**
 * Project the saved `selected` flags onto the defaults. A stored `[]` counts as
 * "never saved" - `Object.keys` of an empty array is 0. Note the `||`: unlike the
 * `pv-widgets` / `pv-feed-results` merges, a default that is selected stays selected
 * whatever the saved blob says.
 */
const mergeViewOptions = (
  defaults: PropertyViewConfig[],
  settings: unknown,
): PropertyViewConfig[] => {
  const isSettingsEmpty =
    !settings ||
    (typeof settings === 'object' && Object.keys(settings).length === 0);

  const saved = (
    isSettingsEmpty ? defaults : (settings as PropertyViewConfig[])
  ) as PropertyViewConfig[];

  return (defaults ?? []).map((option) => {
    const existingOption = saved?.find(
      (v) => v.propertyUri === option.propertyUri,
    );

    return {
      ...option,
      selected: option.selected || existingOption?.selected || false,
    };
  });
};

/**
 * Fetch + merge. `catchError → null` is the degrade-to-defaults path: the merge then
 * runs against an absent blob and yields the defaults.
 */
const fetchViewOptions = (
  api: ViewPresetApiService,
  context: ViewPresetContext,
  defaults: () => Promise<PropertyViewConfig[]>,
): Promise<PropertyViewConfig[]> =>
  Promise.all([
    firstValueFrom(
      api.getViewPresetByContext(context).pipe(catchError(() => of(null))),
    ),
    defaults(),
  ]).then(([viewPreset, defaultConfig]) =>
    mergeViewOptions(defaultConfig, viewPreset?.settings),
  );

function injectUserViewOptionsQuery(
  viewId: ViewId,
  scope: Signal<UserViewOptionsScope | null>,
  defaults: (user: User) => Promise<PropertyViewConfig[]>,
) {
  const api = inject(ViewPresetApiService);

  return injectQuery(() => {
    const current = scope();
    const context = current ? userViewOptionsContext(viewId, current) : null;

    return {
      queryKey: context
        ? viewPresetQueryKey(context, current!.companyId)
        : (['view_preset', viewId, null] as const),
      queryFn: () =>
        fetchViewOptions(api, context!, () => defaults(current!.user)),
      enabled: !!current,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/** Home's view-options dropdown (`hv-objects`). */
export function injectHomeViewOptionsQuery(
  scope: Signal<UserViewOptionsScope | null>,
) {
  const homeSettings = inject(HomeSettingsService);

  return injectUserViewOptionsQuery(ViewId.HomeViewObjects, scope, (user) =>
    homeSettings.getHomeViewConfig(user),
  );
}

/** The workspace screen's view options (`wv-objects`). */
export function injectWorkspaceViewOptionsQuery(
  scope: Signal<UserViewOptionsScope | null>,
) {
  const workspaceSettings = inject(WorkspaceSettingsService);

  return injectUserViewOptionsQuery(
    ViewId.WorkspaceViewObjects,
    scope,
    (user) => workspaceSettings.getWorkspaceViewConfig(user),
  );
}

/**
 * Save one view preset's `settings` blob (POST /view_preset/update_by_id).
 *
 * No toast either way, so a failed save is silent - and it can fail silently
 * *server-side* too: `ViewPresetFacade::processPatchOrPutRequest` swallows a throwing
 * save and still answers 200 with the pre-save row.
 */
export function injectSaveViewPresetMutation() {
  const api = inject(ViewPresetApiService);

  return injectMutation(() => ({
    mutationFn: ({
      context,
      settings,
    }: {
      context: ViewPresetContext;
      settings: unknown;
    }) =>
      firstValueFrom(
        api.saveViewPreset(
          {
            view_id: context.viewId,
            context_object_id: context.contextObjectId,
            context_object_class_id: context.contextObjectClassId,
            name: '',
            settings,
          },
          false,
        ),
      ),
  }));
}

/**
 * Toggle one option of a per-user view-options blob and persist the result. Returns
 * the flipped option so the caller can act on the new value - home's stats option
 * has to invalidate the workspace list.
 */
export function injectToggleUserViewOption(viewId: ViewId) {
  const queryClient = inject(QueryClient);
  const saveMutation = injectSaveViewPresetMutation();

  return (
    scope: UserViewOptionsScope,
    optionName: string,
  ): PropertyViewConfig | undefined => {
    const context = userViewOptionsContext(viewId, scope);
    const queryKey = viewPresetQueryKey(context, scope.companyId);

    const current = queryClient.getQueryData<PropertyViewConfig[]>(queryKey);
    if (!current) {
      return undefined;
    }

    const settings = current.map((option) =>
      option.label === optionName
        ? { ...option, selected: !option.selected }
        : option,
    );

    queryClient.setQueryData(queryKey, settings);
    saveMutation.mutate({ context, settings });

    return settings.find((option) => option.label === optionName);
  };
}

// ─── Dashboard contexts ───────────────────────────────────────────────────
// Both are keyed on the dashboard, so neither takes `companyId`. Unlike the two
// view-options blobs above they carry **scalars, not a defaults-merged array**,
// which is why they get their own hooks rather than a third wrapper.

export const dashboardUIStateContext = (
  dashboardId: string,
): ViewPresetContext => ({
  viewId: ViewId.ObjectViewDashboard,
  contextObjectId: dashboardId,
  contextObjectClassId: NomObjectClass.DASHBOARD,
});

export const dashboardActivePageContext = (
  dashboardId: string,
): ViewPresetContext => ({
  viewId: ViewId.ObjectViewDashboardPage,
  contextObjectId: dashboardId,
  contextObjectClassId: NomObjectClass.DASHBOARD,
});

/** `ov-dashboard` - the page navigator's collapsed/expanded state. */
export function injectDashboardUIStateQuery(
  dashboardId: Signal<string | null>,
) {
  const api = inject(ViewPresetApiService);

  return injectQuery(() => {
    const id = dashboardId();
    const context = id ? dashboardUIStateContext(id) : null;

    return {
      queryKey: context
        ? viewPresetQueryKey(context)
        : (['view_preset', ViewId.ObjectViewDashboard, null] as const),
      queryFn: () =>
        firstValueFrom(
          api.getViewPresetByContext(context!).pipe(catchError(() => of(null))),
        ).then(
          (viewPreset): DashboardViewPreset => ({
            pagination_menu_open:
              (viewPreset?.settings as DashboardViewPreset | undefined)
                ?.pagination_menu_open ?? false,
          }),
        ),
      enabled: !!context,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/** Merge a partial into the cached `ov-dashboard` blob and persist it. */
export function injectUpdateDashboardUIState() {
  const queryClient = inject(QueryClient);
  const saveMutation = injectSaveViewPresetMutation();

  return (dashboardId: string, updates: Partial<DashboardViewPreset>): void => {
    const context = dashboardUIStateContext(dashboardId);
    const queryKey = viewPresetQueryKey(context);

    const settings: DashboardViewPreset = {
      // Fallback for a dashboard with no preset cached yet.
      pagination_menu_open: false,
      ...queryClient.getQueryData<DashboardViewPreset>(queryKey),
      ...updates,
    };

    queryClient.setQueryData(queryKey, settings);
    saveMutation.mutate({ context, settings });
  };
}

/**
 * `ov-dashboard-page` - the dashboard's last selected page.
 *
 * **The one context with no state at all**: its only consumer,
 * `DashboardPageCoordinatorService`, acts on the id once and keeps nothing, and the
 * save is fire-and-forget. Hence the imperative tier rather than a hook - the caller
 * is a root service. The save deliberately does **not** patch the entry: nothing
 * observes it, and the next dashboard open refetches anyway.
 */
export function injectDashboardActivePageTier() {
  const queryClient = inject(QueryClient);
  const api = inject(ViewPresetApiService);
  const saveMutation = injectSaveViewPresetMutation();

  return {
    load: (dashboardId: string): Promise<number | undefined> =>
      queryClient
        .fetchQuery({
          queryKey: viewPresetQueryKey(dashboardActivePageContext(dashboardId)),
          // `?? null`, never undefined: a dashboard with no preset yet answers
          // 200 with an empty body, and TanStack rejects a queryFn that
          // resolves undefined - which used to surface as a load-error toast
          // on every first open of a fresh dashboard.
          queryFn: () =>
            firstValueFrom(
              api.getViewPresetByContext(
                dashboardActivePageContext(dashboardId),
              ),
            ).then(
              (viewPreset) =>
                (
                  viewPreset?.settings as
                    | { active_page_id?: number }
                    | undefined
                )?.active_page_id ?? null,
            ),
          staleTime: 0,
          retry: false,
        })
        .then((pageId) => pageId ?? undefined),

    save: (dashboardId: string, pageId: number): Promise<unknown> =>
      saveMutation.mutateAsync({
        context: dashboardActivePageContext(dashboardId),
        settings: { active_page_id: pageId },
      }),
  };
}

// ─── `pv-widgets` ─────────────────────────────────────────────────────────
// The one blob carrying **two scopes**: `language` (feeds and dashboards) and
// `editMode` (feeds only - WAP-1180's "per Feed per User" compact widget list).
// One shared write path serves both, which is how a stored `editMode: true` can
// reach a dashboard and disable its widget refreshes. The host check that guards
// against it lives in `WidgetViewPresetService.setEditMode`.

/** What the object's `pv-widgets` preset resolves to, defaults already merged. */
export interface WidgetViewPreset {
  language: PropertyViewConfig[];
  editMode: boolean;
}

/** Identity of a `pv-widgets` blob: any object except a workspace. */
export interface WidgetViewPresetScope {
  objectId: string;
  objectClassId: number;
  user: User;
}

export const widgetViewPresetContext = (
  scope: Pick<WidgetViewPresetScope, 'objectId' | 'objectClassId'>,
): ViewPresetContext => ({
  viewId: ViewId.PanelViewWidgets,
  contextObjectId: scope.objectId,
  contextObjectClassId: scope.objectClassId,
});

/**
 * The object's `pv-widgets` preset. The saved `selected` flag wins only where the
 * property matched; otherwise the default keeps its own.
 */
export function injectWidgetViewPresetQuery(
  scope: Signal<WidgetViewPresetScope | null>,
) {
  const api = inject(ViewPresetApiService);
  const widgetSettings = inject(WidgetSettingsService);

  return injectQuery(() => {
    const current = scope();
    const context = current ? widgetViewPresetContext(current) : null;

    return {
      queryKey: context
        ? viewPresetQueryKey(context)
        : (['view_preset', ViewId.PanelViewWidgets, null] as const),
      queryFn: (): Promise<WidgetViewPreset> =>
        Promise.all([
          firstValueFrom(
            api
              .getViewPresetByContext(context!)
              .pipe(catchError(() => of(null))),
          ),
          widgetSettings.getWidgetLanguageConfig(current!.user),
        ]).then(([viewPreset, defaultLanguageConfig]) => {
          const settings = viewPreset?.settings as
            | { language?: PropertyViewConfig[]; editMode?: boolean }
            | undefined;

          const saved = settings?.language;

          return {
            language: (defaultLanguageConfig ?? []).map((option) => {
              const existingOption = saved?.find(
                (v) => v.propertyUri === option.propertyUri,
              );

              return existingOption
                ? { ...option, selected: existingOption.selected }
                : option;
            }),
            editMode: settings?.editMode ? settings.editMode : false,
          };
        }),
      enabled: !!context,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

// ─── `pv-feed-results` ────────────────────────────────────────────────────
// The feed's card view configuration - the toolbar's View dropdown. Same
// cache-as-state shape as the two view-options blobs, but with the `pv-widgets`
// merge variant, an access-level filter on the defaults, and a radio-group-aware
// toggle.

/** Identity of a `pv-feed-results` blob: the feed plus what the defaults need. */
export interface FeedViewConfigScope {
  feedId: string;
  objectTypeId: NomObjectTypes;
  /**
   * Not a key term: it only feeds the defaults' permission + access-level filtering,
   * and the feed id already identifies the blob company-uniquely.
   */
  user: User;
}

export const feedViewConfigContext = (feedId: string): ViewPresetContext => ({
  viewId: ViewId.PaneViewFeedResults,
  contextObjectId: feedId,
  contextObjectClassId: NomObjectClass.FEED,
});

export interface FeedViewConfig {
  config: PropertyViewConfig[];
  /**
   * True when the feed had a saved view preset, so `config.selected` came from it
   * rather than from the defaults. The `post_time_mode` radio is only trustworthy
   * when this is true - see the toolbar, which otherwise defers to the store's
   * feed-derived `timeShown`.
   */
  fromPreset: boolean;
}

/**
 * **The order below is load-bearing:** iterate the *filtered* defaults and look the
 * saved flags up in them. That is what keeps `post_completeness_score` out of the
 * result for a non-GLOBAL_ADMIN - flip the direction and non-admins get the column
 * back.
 */
const mergeFeedViewConfig = (
  defaults: PropertyViewConfig[],
  settings: unknown,
  user: User,
  feedTimeShown: string,
): FeedViewConfig => {
  const isSettingsEmpty =
    !settings ||
    (typeof settings === 'object' && Object.keys(settings).length === 0);

  const saved = (
    isSettingsEmpty ? defaults : (settings as PropertyViewConfig[])
  ) as PropertyViewConfig[];

  const visible =
    user?.access_level_id < UserAccessLevel.GLOBAL_ADMIN
      ? (defaults ?? []).filter(
          (item) => item.propertyUri !== 'post_completeness_score',
        )
      : (defaults ?? []);

  const merged = visible.map((option) => {
    const existingOption = saved?.find(
      (v) => v.propertyUri === option.propertyUri,
    );

    return existingOption
      ? { ...option, selected: existingOption.selected }
      : option;
  });

  // ! Without a preset the defaults' own `post_time_mode` flag is meaningless -
  // ! it always marks published-absolute, whatever the feed is set to. Point it at
  // ! the feed instead: post cards read the date off these flags in some layouts
  // ! and off the store's `timeShown` in others, and the two have to agree.
  const config = isSettingsEmpty
    ? merged.map((option) =>
        option.radioGroup === 'post_time_mode'
          ? { ...option, selected: option.path === feedTimeShown }
          : option,
      )
    : merged;

  return { config, fromPreset: !isSettingsEmpty };
};

/**
 * The feed results toolbar's View dropdown (`pv-feed-results`). Owned by its two
 * consumers directly (`feed.component` and `feed-results-toolbar`) rather than by a
 * service: nothing outside those two reads it, and component-scoped observers are
 * what give it a per-visit refetch.
 */
export function injectFeedViewConfigQuery(
  feed: Signal<{
    id: string;
    object_type_id: number;
    // Only read to flag the matching `post_time_mode` radio when the feed has no
    // saved preset - see `mergeFeedViewConfig`.
    post_time_field?: string;
    post_time_mode?: string;
  } | null>,
  user: Signal<User | null>,
) {
  const api = inject(ViewPresetApiService);
  const feedSettings = inject(FeedSettingsService);

  return injectQuery(() => {
    const currentFeed = feed();
    const currentUser = user();
    const scope: FeedViewConfigScope | null =
      currentFeed && currentUser
        ? {
            feedId: currentFeed.id,
            objectTypeId: currentFeed.object_type_id,
            user: currentUser,
          }
        : null;
    const context = scope ? feedViewConfigContext(scope.feedId) : null;

    return {
      queryKey: context
        ? viewPresetQueryKey(context)
        : (['view_preset', ViewId.PaneViewFeedResults, null] as const),
      queryFn: (): Promise<FeedViewConfig> =>
        Promise.all([
          firstValueFrom(
            api
              .getViewPresetByContext(context!)
              .pipe(catchError(() => of(null))),
          ),
          feedSettings.getFeedConfig(scope!.objectTypeId, scope!.user),
        ]).then(([viewPreset, defaults]) =>
          mergeFeedViewConfig(
            defaults,
            viewPreset?.settings,
            scope!.user,
            feedDerivedTimeShown(currentFeed!),
          ),
        ),
      enabled: !!context,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * Toggle one option of the feed's view configuration and persist the result.
 *
 * **One quirk, kept deliberately rather than fixed:** a radio option outside
 * the `post_time_mode` group flips nothing at all. The toolbar's
 * `view_posts_mode` radios never reach here (they go through
 * `injectSetFeedPostViewModeMutation`), which is why the gap never showed.
 */
export function injectToggleFeedViewConfigOption() {
  const queryClient = inject(QueryClient);
  const saveMutation = injectSaveViewPresetMutation();

  return (feedId: string, optionName: string): void => {
    const context = feedViewConfigContext(feedId);
    const queryKey = viewPresetQueryKey(context);

    const current = queryClient.getQueryData<FeedViewConfig>(queryKey);
    if (!current) return;

    const option = current.config.find((item) => item.label === optionName);
    if (!option) return;

    const settings = current.config.map((item) => {
      if (!option.isRadio) {
        return item.label === optionName
          ? { ...item, selected: !item.selected }
          : item;
      }

      return option.radioGroup === 'post_time_mode' &&
        item.radioGroup === 'post_time_mode'
        ? { ...item, selected: item.label === optionName }
        : item;
    });

    // `fromPreset` is true from here on: this toggle *is* the preset write, so the
    // config's `post_time_mode` selection is authoritative once it lands.
    queryClient.setQueryData<FeedViewConfig>(queryKey, {
      config: settings,
      fromPreset: true,
    });

    saveMutation.mutate({ context, settings });
  };
}

// ─── ag-grid column presets (`ag-*` view ids) ─────────────────────────────
// The grid toolbar's preset combobox. Unlike the per-context blobs above this is
// a plain **list** per grid (GET /view_preset/get_by_view_id): every preset the
// user saved for one `viewId`, each carrying `settings.columnState` +
// `settings.filterModel`, which `DataGridComponent` applies. The two
// mutations patch the cached list in place - the response is the saved row, so
// no refetch - and own the toasts. Background refetching is off: a list landing
// mid-session would re-run the grid's default-preset application.

export const gridViewPresetsQueryKey = (viewId: ViewId) =>
  ['view_preset', 'grid', viewId] as const;

/** Every preset saved for one grid. `viewId` is a signal because a grid subclass assigns it after the base constructor ran. */
export function injectGridViewPresetsQuery(viewId: Signal<ViewId | null>) {
  const api = inject(ViewPresetApiService);

  return injectQuery(() => {
    const id = viewId();

    return {
      queryKey: id
        ? gridViewPresetsQueryKey(id)
        : (['view_preset', 'grid', null] as const),
      queryFn: () => firstValueFrom(api.loadAvalableViewPresets(id!)),
      enabled: !!id,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * Re-derive `is_effective_default` after a cache patch. The backend flags it only on
 * a `get_by_view_id` list (the default row with the highest scope id: Mine over
 * Company over System) and never on the single row a save or a flag flip returns,
 * so an upsert of that row would otherwise leave the opener marker stale.
 */
const withEffectiveDefault = (presets: ViewPreset[]): ViewPreset[] => {
  const opener = presets
    .filter((p) => p.is_default)
    .sort(
      (a, b) => (b.view_preset_scope_id ?? 0) - (a.view_preset_scope_id ?? 0),
    )[0];

  return presets.map((p) => ({
    ...p,
    is_effective_default: !!opener && p.uuid === opener.uuid,
  }));
};

/** Patch one grid's cached preset list, keeping `dataUpdatedAt` (see api-layer.md). */
const patchGridViewPresets = (
  queryClient: QueryClient,
  viewId: ViewId,
  patch: (presets: ViewPreset[]) => ViewPreset[],
): void => {
  const queryKey = gridViewPresetsQueryKey(viewId);
  const updatedAt =
    queryClient.getQueryState<ViewPreset[]>(queryKey)?.dataUpdatedAt;

  queryClient.setQueryData<ViewPreset[]>(
    queryKey,
    (current) => patch(current ?? []),
    updatedAt ? { updatedAt } : undefined,
  );
};

/**
 * Create (`saveAsNew`, PUT) or update (POST) one grid preset and upsert the
 * response into the grid's cached list.
 */
export function injectSaveGridViewPresetMutation() {
  const api = inject(ViewPresetApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      viewPreset,
      saveAsNew,
    }: {
      viewPreset: ViewPreset;
      saveAsNew: boolean;
    }) => firstValueFrom(api.saveViewPreset(viewPreset, saveAsNew)),
    onSuccess: (saved, { viewPreset }) => {
      patchGridViewPresets(
        queryClient,
        viewPreset.view_id as ViewId,
        (presets) =>
          withEffectiveDefault(
            presets.some((p) => p.uuid === saved.uuid)
              ? presets.map((p) => (p.uuid === saved.uuid ? saved : p))
              : [...presets, saved],
          ),
      );
      toast.showSuccess(
        translate.instant('grid.settings.save.your-settings-have-being-saved'),
      );
    },
    onError: () =>
      toast.showError(translate.instant('grid.settings.save.error-message')),
  }));
}

/**
 * Flip `is_default` and/or `is_pinned` on one grid preset (the toolbar's star,
 * next the pin control and the tab strip's unpin) through PATCH
 * /view_preset/set_flags/{uuid} - flags only, the settings never travel. The
 * backend keeps one default per user + company + view
 * (`ViewPresetFacade::clearOtherDefaults`), so one call suffices and the cache
 * patch mirrors that rule: the saved row is upserted and, when it became the
 * default, every other row loses the flag. Quiet on purpose - a toggle needs no
 * toast; a failure only means the next visit opens with the previous state.
 */
export function injectSetGridViewPresetFlagsMutation() {
  const api = inject(ViewPresetApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: ({
      viewPreset,
      flags,
    }: {
      viewPreset: ViewPreset;
      flags: ViewPresetFlags;
    }) => firstValueFrom(api.setViewPresetFlags(viewPreset.uuid, flags)),
    onSuccess: (saved, { viewPreset }) => {
      patchGridViewPresets(
        queryClient,
        viewPreset.view_id as ViewId,
        (presets) =>
          withEffectiveDefault(
            presets.map((p) =>
              p.uuid === saved.uuid
                ? saved
                : saved.is_default &&
                    p.is_default &&
                    p.view_preset_scope_id === saved.view_preset_scope_id
                  ? { ...p, is_default: false }
                  : p,
            ),
          ),
      );
    },
  }));
}

/** Delete one grid preset and drop it from the grid's cached list. */
export function injectDeleteGridViewPresetMutation() {
  const api = inject(ViewPresetApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({ uuid }: { uuid: string; viewId: ViewId }) =>
      firstValueFrom(api.deleteSettings(uuid)),
    onSuccess: (_, { uuid, viewId }) => {
      patchGridViewPresets(queryClient, viewId, (presets) =>
        presets.filter((p) => p.uuid !== uuid),
      );
      toast.showSuccess(
        translate.instant(
          'grid.settings.delete.your-settings-have-being-deleted',
        ),
      );
    },
    onError: () =>
      toast.showError(translate.instant('grid.settings.delete.error-message')),
  }));
}

/**
 * Add a shared (System / Company) preset to one grid's cached list: the row an
 * `admin_create` or `admin_copy` returns is visible to the caller through
 * `get_by_view_id` too, so the list is patched in place rather than refetched.
 */
const upsertSharedGridViewPreset = (
  queryClient: QueryClient,
  viewId: ViewId,
  shared: ViewPreset,
): void =>
  patchGridViewPresets(queryClient, viewId, (presets) =>
    withEffectiveDefault(
      presets.some((p) => p.uuid === shared.uuid)
        ? presets.map((p) => (p.uuid === shared.uuid ? shared : p))
        : [...presets, shared],
    ),
  );

/**
 * Create a System or Company preset from the grid's current settings through
 * PUT /view_preset/admin_create (WAP-1631 item 7). Owns the cache upsert and the
 * toasts; a 403 (caller below the scope's admin level) surfaces as the error toast.
 */
export function injectAdminCreateGridViewPresetMutation() {
  const api = inject(ViewPresetApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (viewPreset: ViewPreset) =>
      firstValueFrom(api.adminCreateViewPreset(viewPreset)),
    onSuccess: (shared, viewPreset) => {
      upsertSharedGridViewPreset(
        queryClient,
        viewPreset.view_id as ViewId,
        shared,
      );
      toast.showSuccess(
        translate.instant('grid.settings.share.the-preset-is-now-shared'),
      );
    },
    onError: () =>
      toast.showError(translate.instant('grid.settings.share.error-message')),
  }));
}

/**
 * Promote one of the caller's own grid presets into the System or Company scope
 * through POST /view_preset/admin_copy (the former admin_promote). The source
 * stays; the new shared row joins the cached list.
 */
export function injectAdminCopyGridViewPresetMutation() {
  const api = inject(ViewPresetApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      viewPreset,
      scopeId,
    }: {
      viewPreset: ViewPreset;
      scopeId: number;
    }) =>
      firstValueFrom(
        api.adminCopyViewPreset({
          uuid: viewPreset.uuid,
          view_preset_scope_id: scopeId,
        }),
      ),
    onSuccess: (shared, { viewPreset }) => {
      upsertSharedGridViewPreset(
        queryClient,
        viewPreset.view_id as ViewId,
        shared,
      );
      toast.showSuccess(
        translate.instant('grid.settings.share.the-preset-is-now-shared'),
      );
    },
    onError: () =>
      toast.showError(translate.instant('grid.settings.share.error-message')),
  }));
}

// * Admin -> View Presets grid (WAP-1638) ==================================
// The grid's rows live in ag-grid's SSRM block cache, so these hooks own the
// toasts and the shared-list cache patch (a Company / System row is visible to
// the caller's own grids through get_by_view_id too); the grid patches its row
// from the response in a per-call callback - the Solr index is refreshed by an
// async worker, so an immediate re-query would still show the old row.

/** Rename / describe a shared row: POST /view_preset/admin_update. */
export function injectAdminUpdateViewPresetMutation() {
  const api = inject(ViewPresetApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (update: AdminViewPresetUpdate) =>
      firstValueFrom(api.adminUpdateViewPreset(update)),
    onSuccess: (updated) => {
      upsertSharedGridViewPreset(
        queryClient,
        updated.view_id as ViewId,
        updated,
      );
      toast.showSuccess(
        translate.instant('view-presets-grid.msg.the-preset-was-renamed'),
      );
    },
    onError: () =>
      toast.showError(
        translate.instant('view-presets-grid.msg.renaming-the-preset-failed'),
      ),
  }));
}

/** `is_default` / `is_pinned` on a shared row: PATCH /view_preset/admin_set_flags/{uuid}. */
export function injectAdminSetViewPresetFlagsMutation() {
  const api = inject(ViewPresetApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({ uuid, flags }: { uuid: string; flags: ViewPresetFlags }) =>
      firstValueFrom(api.adminSetViewPresetFlags(uuid, flags)),
    onSuccess: (updated) => {
      upsertSharedGridViewPreset(
        queryClient,
        updated.view_id as ViewId,
        updated,
      );
    },
    onError: () =>
      toast.showError(
        translate.instant('view-presets-grid.msg.updating-the-flags-failed'),
      ),
  }));
}

/** Copy a row into the System or Company scope from the admin grid: POST /view_preset/admin_copy. */
export function injectAdminCopyViewPresetMutation() {
  const api = inject(ViewPresetApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (copy: AdminViewPresetCopy) =>
      firstValueFrom(api.adminCopyViewPreset(copy)),
    onSuccess: (created) => {
      upsertSharedGridViewPreset(
        queryClient,
        created.view_id as ViewId,
        created,
      );
      toast.showSuccess(
        translate.instant('view-presets-grid.msg.the-preset-was-copied'),
      );
    },
    onError: (error: HttpErrorResponse) =>
      toast.showError(
        error?.status === 400
          ? translate.instant(
              'view-presets-grid.msg.a-preset-with-this-name-already-exists',
            )
          : translate.instant('view-presets-grid.msg.copying-the-preset-failed'),
      ),
  }));
}

/** Delete a shared row: DELETE /view_preset/admin_delete/{uuid}. */
export function injectAdminDeleteViewPresetMutation() {
  const api = inject(ViewPresetApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (viewPreset: AdminViewPreset) =>
      firstValueFrom(api.adminDeleteViewPreset(viewPreset.uuid)),
    onSuccess: (_result, viewPreset) => {
      patchGridViewPresets(
        queryClient,
        viewPreset.view_id as ViewId,
        (presets) =>
          withEffectiveDefault(
            presets.filter((p) => p.uuid !== viewPreset.uuid),
          ),
      );
      toast.showSuccess(
        translate.instant('view-presets-grid.msg.the-preset-was-deleted'),
      );
    },
    onError: () =>
      toast.showError(
        translate.instant('view-presets-grid.msg.deleting-the-preset-failed'),
      ),
  }));
}
